import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

/** The admin user- and org-search endpoints' page-size cap. */
const MAX_IDS = 200;

// 30 seconds, the member lists' original window: an admin editing membership
// or names in another tab expects the labels to catch up promptly.
const STALE_TIME_MS = 30_000;

/**
 * Display labels for user-group member ids — ``"User: <name> (<user_id>)"``
 * or ``"Org: <name> (<org_id>)"`` — resolved with one cross-org user lookup
 * and one org lookup per id set, run in parallel.
 *
 * A member id may name a user or an org, and nothing on the record says
 * which, so every id goes to both lookups; each simply doesn't match ids of
 * the other kind. Ids that match neither, or fall beyond the endpoints' page
 * cap, are absent from the result, so callers fall back to the raw id. An
 * empty id set issues no request.
 */
export function useMemberLabels(
  memberIds: readonly string[],
): ReadonlyMap<string, string> {
  const { adminService, orgService } = useDomainServices();
  const ids = React.useMemo(
    () => Array.from(new Set(memberIds)).sort().slice(0, MAX_IDS),
    [memberIds],
  );

  const { data } = useQuery({
    queryKey: ["user-group-member-labels", ids],
    queryFn: async ({ signal }) => {
      const [userPage, orgs] = await Promise.all([
        adminService.searchAdminUsers(
          {
            condition: {
              operator: "OR",
              conditions: ids.map((id) => ({
                field: "user_id",
                comparator: "EQUALS" as const,
                value: id,
              })),
            },
            // An exact id set must come back in one page.
            limit: ids.length,
          },
          { abortSignal: signal },
        ),
        orgService.getOrgsByIds(ids, { abortSignal: signal }),
      ]);
      // A plain object rather than a Map, so structural sharing keeps the
      // same reference while the resolved labels are unchanged.
      const labels: Record<string, string> = {};
      for (const user of userPage.items) {
        labels[user.user_id] = user.name
          ? `User: ${user.name} (${user.user_id})`
          : `User: ${user.user_id}`;
      }
      for (const org of orgs) {
        labels[org.org_id] = `Org: ${org.name} (${org.org_id})`;
      }
      return labels;
    },
    enabled: ids.length > 0,
    staleTime: STALE_TIME_MS,
    // Keep the previous labels while a changed id set resolves, so the
    // Members column doesn't flash raw ids after every edit.
    placeholderData: keepPreviousData,
  });

  return React.useMemo(() => new Map(Object.entries(data ?? {})), [data]);
}
